/*
  TegraHandel Tiered Pricing (Quantity-only)
  ------------------------------------------
  - Shows calculator ONLY when qty >= 2
  - Hides Shopify base price when qty >= 2
  - Shows: crossed old total, new total, saving, and unit price line (smaller text)
  - Reads metafield JSON from <script data-tegra-tier-pricing> (custom.tier_pricing)
*/

(function () {
  'use strict';

  const PRICE_SUMMARY_ID = 'tegra-tiered-price-summary';

  // ✅ German labels
  const I18N = {
    saving: 'Ersparnis',
    unit: '1 Stk.:',
  };

  function readTierData(root) {
    const node = root.querySelector('script[data-tegra-tier-pricing]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (e) {
      console.error('[tegra-tiered] Failed to parse tier pricing JSON', e);
      return null;
    }
  }

  function moneyFormatCents(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    const eur = (cents / 100).toFixed(2).replace('.', ',');
    return eur + ' €';
  }

  function toCents(amountStr) {
    if (amountStr == null) return null;
    const n = Number(String(amountStr).replace(',', '.'));
    if (!Number.isFinite(n)) return null;
    return Math.round(n * 100);
  }

  // ✅ Get sorted numeric breaks from tiers.break_totals keys
  function getTierBreaks(tiers) {
    const bt = tiers?.break_totals;
    if (!bt || typeof bt !== 'object') return [];
    return Object.keys(bt)
      .map((k) => Number(String(k).trim()))
      .filter((n) => Number.isFinite(n) && n > 0)
      .sort((a, b) => a - b);
  }

  // ✅ Find nearest available tier <= qty (caps to max tier)
  function nearestTierQtyDynamic(tiers, qty) {
    const breaks = getTierBreaks(tiers);
    if (!breaks.length) return null;

    // if qty is invalid, fallback to first break
    if (!Number.isFinite(qty) || qty <= breaks[0]) return breaks[0];

    // cap above max break
    const maxBreak = breaks[breaks.length - 1];
    if (qty >= maxBreak) return maxBreak;

    // floor to the biggest break <= qty
    for (let i = breaks.length - 1; i >= 0; i--) {
      if (qty >= breaks[i]) return breaks[i];
    }
    return breaks[0];
  }

  function findQuantityBlock(root) {
    return root.querySelector('[id^="Quantity-Form-"]') || root.querySelector('.product-form__quantity');
  }

  function findQuantityInput(root) {
    return root.querySelector('input[name="quantity"]');
  }

  function findPriceContainer(root) {
    return (
      root.querySelector('div.price') ||
      root.querySelector('.product__info-container .price') ||
      root.querySelector('[data-price]')
    );
  }

  function ensurePriceSummary(priceContainer) {
    if (!priceContainer) return null;

    let el = priceContainer.querySelector(`#${PRICE_SUMMARY_ID}`);
    if (!el) {
      el = document.createElement('div');
      el.id = PRICE_SUMMARY_ID;
      el.className = 'tegra-tiered-price-summary';
      priceContainer.appendChild(el);
    }
    return el;
  }

  // Hide/show Shopify base price nodes when calculator is active
  function toggleBasePrice(priceContainer, hide) {
    if (!priceContainer) return;

    const candidates = [
      ...priceContainer.querySelectorAll(
        [
          '.price__regular .price-item--regular',
          '.price-item--regular',
          '.price-item--sale',
          '.price__sale .price-item--sale',
          '.price__regular',
          '.price__sale',
          '.price-item',
          '[data-regular-price]',
          '[data-sale-price]',
        ].join(',')
      ),
    ];

    if (!candidates.length) return;

    for (const el of candidates) {
      // Don't hide our own summary
      if (el.id === PRICE_SUMMARY_ID || el.closest(`#${PRICE_SUMMARY_ID}`)) continue;

      if (hide) {
        if (el.dataset.tegraOrigDisplay == null) el.dataset.tegraOrigDisplay = el.style.display || '';
        el.style.display = 'none';
      } else {
        if (el.dataset.tegraOrigDisplay != null) {
          el.style.display = el.dataset.tegraOrigDisplay;
          delete el.dataset.tegraOrigDisplay;
        } else {
          el.style.display = '';
        }
      }
    }
  }

  function computePricing(tiers, qty) {
    if (!tiers || !tiers.break_totals) return null;

    // ✅ Do not show calculator for qty <= 1
    if (!Number.isFinite(qty) || qty <= 1) return null;

    // ✅ Base (1 pcs) must exist
    const baseTotalCents = toCents(tiers.break_totals['1'] ?? tiers.break_totals[1]);
    if (!Number.isFinite(baseTotalCents)) return null;

    const baseUnitCents = baseTotalCents;

    // ✅ Dynamic tier selection based on actual breaks
    const tierQty = nearestTierQtyDynamic(tiers, qty);
    if (!Number.isFinite(tierQty)) return null;

    const tierTotalCents = toCents(tiers.break_totals[String(tierQty)] ?? tiers.break_totals[tierQty]);
    if (!Number.isFinite(tierTotalCents)) return null;

    // Unit price from chosen tier
    const tierUnitCents = Math.round(tierTotalCents / tierQty);

    const oldTotalCents = baseUnitCents * qty;
    const newTotalCents = tierUnitCents * qty;
    const savingCents = Math.max(0, oldTotalCents - newTotalCents);

    return { oldTotalCents, newTotalCents, savingCents, tierUnitCents };
  }

  function renderPriceSummary(summaryEl, pricing) {
    if (!summaryEl) return;

    // ✅ Hide everything if not active
    if (!pricing) {
      summaryEl.innerHTML = '';
      summaryEl.style.display = 'none';
      return;
    }

    summaryEl.style.display = '';

    const oldStr = moneyFormatCents(pricing.oldTotalCents);
    const newStr = moneyFormatCents(pricing.newTotalCents);
    const saveStr = moneyFormatCents(pricing.savingCents);
    const unitStr = moneyFormatCents(pricing.tierUnitCents);

    summaryEl.innerHTML = `
      <div class="tegra-tiered-line tegra-tiered-totals">
        <s class="tegra-tiered-old">${oldStr}</s>
        <span class="tegra-tiered-new">${newStr}</span>
      </div>
      <div class="tegra-tiered-line tegra-tiered-saving">
        <span class="tegra-tiered-saving-label">${I18N.saving}</span>
        <span class="tegra-tiered-saving-value">${saveStr}</span>
      </div>
      <div class="tegra-tiered-line tegra-tiered-unit" style="font-size:0.9em; opacity:0.9;">
        <span>${I18N.unit}</span>
        <span class="tegra-tiered-unit-value">${unitStr}</span>
      </div>
    `;
  }

  function initOnRoot(root) {
    if (!root || root.nodeType !== 1) return;
    if (root.dataset.tegraTieredInit === '1') return;
    root.dataset.tegraTieredInit = '1';

    const qtyInput = findQuantityInput(root);
    const qtyBlock = findQuantityBlock(root);
    if (!qtyInput || !qtyBlock) return;

    const tierData = readTierData(root);
    const tiers = tierData?.tiers || tierData;

    function applyUI() {
      const q = parseInt(qtyInput.value || '1', 10) || 1;

      // ✅ Themes often re-render price nodes; re-find each time
      const priceContainer = findPriceContainer(root) || findPriceContainer(document);
      const summaryEl = ensurePriceSummary(priceContainer);

      const pricing = computePricing(tiers, q);
      renderPriceSummary(summaryEl, pricing);

      // ✅ Hide base price when calculator active (qty>=2)
      toggleBasePrice(priceContainer, q >= 2);
    }

    qtyInput.addEventListener('change', applyUI);
    qtyInput.addEventListener('input', applyUI);

    // Some themes re-render price on variant changes; we re-apply
    root.addEventListener('variant:change', applyUI);
    document.addEventListener('variant:change', () => {
      if (document.contains(root)) applyUI();
    });

    applyUI();
  }

  function scanAndInit() {
    const productForms = document.querySelectorAll('product-form');
    for (const pf of productForms) {
      const root = pf.closest('section') || pf.closest('div') || pf;
      initOnRoot(root);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scanAndInit);
  } else {
    scanAndInit();
  }

  document.addEventListener('shopify:section:load', scanAndInit);
  document.addEventListener('shopify:section:reorder', scanAndInit);
})();

